import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useLanguage } from './i18n';
import RegisterForm from './RegisterForm';
import LoginForm from './LoginForm';
import './RoleSelector.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const T = {
  kk: {
    header: '🎭 Демо-режим',
    subtitle: 'Қала оқиғаларын басқару жүйесін сынау',
    chooseRole: 'Рөліңізді таңдаңыз:',
    chooseService: 'Қызмет түрін таңдаңыз:',
    errSelectRole: 'Рөлді таңдаңыз',
    errSelectService: 'Қызмет түрін таңдаңыз',
    errAuth: 'Авторизация қатесі: ',
    egovSoon: 'eGov интеграциясы келесі нұсқада қолжетімді болады',
    guest: '👁️ Қонақ',
    loggingIn: 'Кіру...',
    demoBtn: '🎭 Демо-режим'
  },
  ru: {
    header: '🎭 Демо-режим',
    subtitle: 'Тестирование системы управления событиями города',
    chooseRole: 'Выберите вашу роль:',
    chooseService: 'Выберите тип службы:',
    errSelectRole: 'Пожалуйста, выберите роль',
    errSelectService: 'Пожалуйста, выберите тип службы',
    errAuth: 'Ошибка авторизации: ',
    egovSoon: 'eGov интеграция будет доступна в следующей версии',
    guest: '👁️ Гость',
    loggingIn: 'Вход...',
    demoBtn: '🎭 Демо-режим'
  }
};

const RoleSelector = ({ onBackToAuth }) => {
  const { login } = useAuth();
  const { language } = useLanguage();
  const t = T[language];
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);

  const roles = [
    {
      id: 'resident',
      title_kk: 'Тұрғын',
      title_ru: 'Житель',
      description_kk: 'Қала тұрғыны ретінде оқиғаларды көру және жасау',
      description_ru: 'Просматривать и создавать события как житель города',
      icon: '👤',
      color: '#6b7c6e'
    },
    {
      id: 'service',
      title_kk: 'Қызмет',
      title_ru: 'Служба',
      description_kk: 'Оқиғаларды өңдеу және дайындау',
      description_ru: 'Обрабатывать события и добавлять статусы выполнения',
      icon: '🔧',
      color: '#4a90a4'
    },
    {
      id: 'admin',
      title_kk: 'Әкім',
      title_ru: 'Аким',
      description_kk: 'Қаланы басқару және барлық оқиғаларды көзету',
      description_ru: 'Управление городом и контроль всех событий',
      icon: '👨‍💼',
      color: '#c92a2a'
    }
  ];

  // Load services when component mounts
  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (err) {
      console.error('Error loading services:', err);
    }
  };

  const handleRegistrationSuccess = (data) => {
    setRegistrationSuccess(data);
    setShowRegister(false);
  };

  const handleLoginSuccess = async (data) => {
    // Токен сохранён сервером в httpOnly cookie
    setShowLogin(false);
    window.location.reload(); // Simple reload to update app state
  };

  const handleDemoLogin = async () => {
    if (!selectedRole) {
      setError(t.errSelectRole);
      return;
    }

    if (selectedRole === 'service' && !selectedServiceType) {
      setError(t.errSelectService);
      return;
    }

    setLoading(true);
    setError('');

    const result = await login(selectedRole, selectedServiceType);
    
    if (!result.success) {
      setError(t.errAuth + result.error);
      setLoading(false);
    } else {
      // Success - clear modals and let AuthContext handle the state change
      setShowLogin(false);
      setShowRegister(false);
      setLoading(false);
      console.log('Demo login successful, AuthContext will handle redirect...');
      // No page reload needed - AuthContext will detect the token and update user state
    }
  };

  return (
    <div className="role-selector-container">
      <div className="role-selector">
        <div className="role-selector-header">
          <h1>{t.header}</h1>
          <p>{t.subtitle}</p>
        </div>

        <div className="role-selection">
          <h2>{t.chooseRole}</h2>
          
          <div className="roles-grid">
            {roles.map((role) => (
              <div
                key={role.id}
                className={`role-card ${selectedRole === role.id ? 'selected' : ''}`}
                onClick={() => {
                  console.log('Role selected:', role.id);
                  setSelectedRole(role.id);
                  if (role.id !== 'service') {
                    setSelectedServiceType('');
                  }
                }}
                style={{ '--role-color': role.color }}
              >
                <div className="role-icon">{role.icon}</div>
                <div className="role-content">
                  <h3 className="role-title">
                    {role.title_kk} / {role.title_ru}
                  </h3>
                  <p className="role-description-kk">{role.description_kk}</p>
                  <p className="role-description-ru">{role.description_ru}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Service type selection */}
          {selectedRole === 'service' && (
            <div className="service-selection">
              <h3>{t.chooseService}</h3>
              <div className="services-grid">
                {services.map((service) => (
                  <div
                    key={service.id}
                    className={`service-card ${selectedServiceType === service.id ? 'selected' : ''}`}
                    onClick={() => setSelectedServiceType(service.id)}
                  >
                    <div className="service-content">
                      <h4>{language === 'kk' ? (service.name_kk || service.name_ru) : service.name_ru}</h4>
                      <p className="service-description">{service.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Three main auth options only */}
          <div className="main-auth-section">
            <div className="auth-buttons-ordered">
              <button
                className="egov-button"
                onClick={() => alert(t.egovSoon)}
              >
                🏛️ eGov Mobile/Digital ID
              </button>
              <button
                className="guest-button"
                onClick={() => {
                  // Handle guest mode - should go to main app without login
                  window.location.href = '/?guest=true';
                }}
              >
                👁️ {t.guest.replace('👁️ ', '')}
              </button>
              <button
                className="demo-login-button"
                onClick={() => {
                  console.log('Demo login clicked, selectedRole:', selectedRole, 'loading:', loading);
                  handleDemoLogin();
                }}
                disabled={loading || !selectedRole || (selectedRole === 'service' && !selectedServiceType)}
              >
                {loading ? t.loggingIn : t.demoBtn}
              </button>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showRegister && (
          <RegisterForm
            onClose={() => setShowRegister(false)}
            onSuccess={handleRegistrationSuccess}
            initialRole={selectedRole}
          />
        )}

        {showLogin && (
          <LoginForm
            onClose={() => setShowLogin(false)}
            onSuccess={handleLoginSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default RoleSelector;